import type { Column, Kind, Table } from './columns.ts'
import { syncedTables, tablesByName, type TableName } from './tables.ts'

// Local SQLite schema, generated from the registry. The local database is a
// per-tenant cache of synced rows; the server enforces all business rules, so
// locally we only keep types, NOT NULL and the indexes the UI queries need.

/**
 * Bump when the registry or anything below changes, and update
 * LOCAL_SCHEMA_FINGERPRINT (sqlite.test.ts fails with the new value).
 * A version change makes packages/local rebuild the synced tables from the
 * server (the outbox is kept), so no hand-written SQLite migrations are needed.
 */
export const LOCAL_SCHEMA_VERSION = 32
export const LOCAL_SCHEMA_FINGERPRINT = '5fb5807d'

const SQLITE_TYPE: Record<Kind, 'TEXT' | 'INTEGER'> = {
  uuid: 'TEXT',
  text: 'TEXT',
  int: 'INTEGER',
  bigint: 'INTEGER',
  bool: 'INTEGER',
  date: 'TEXT',
  timestamptz: 'TEXT',
  json: 'TEXT',
}

/** Indexes for local queries (no tenant_id: each local DB holds one tenant). */
export const LOCAL_INDEXES: Partial<Record<TableName, readonly (readonly string[])[]>> = {
  categories: [['parent_id']],
  customers: [['name']],
  suppliers: [['name']],
  products: [
    ['sku'],
    ['barcode'],
    ['name'],
    ['category_id'],
    ['brand_id'],
    ['parent_id'],
    ['plu_code'],
  ],
  dining_tables: [['area_id']],
  orders: [['status'], ['table_id'], ['invoice_id'], ['server_id']],
  order_items: [['order_id']],
  kot_tickets: [['order_id'], ['status']],
  deliveries: [['order_id'], ['status']],
  order_requests: [['status'], ['table_id'], ['order_id']],
  order_request_items: [['request_id']],
  kot_items: [['kot_id'], ['order_item_id']],
  product_codes: [['code'], ['product_id']],
  product_components: [['product_id'], ['component_id']],
  batches: [['product_id'], ['expiry_date']],
  product_serials: [['product_id', 'status'], ['serial'], ['sold_line_id']],
  counter_sequences: [['counter_id', 'doc_type', 'fiscal_year']],
  purchase_orders: [['supplier_id'], ['order_date']],
  purchase_order_lines: [['purchase_order_id']],
  goods_receipts: [['purchase_order_id'], ['supplier_id'], ['receipt_date']],
  goods_receipt_lines: [['goods_receipt_id'], ['purchase_order_line_id']],
  sales_orders: [['kind', 'doc_date'], ['customer_id'], ['source_id']],
  sales_order_lines: [['sales_order_id']],
  sales_invoices: [['invoice_date'], ['customer_id'], ['status'], ['number'], ['sales_order_id']],
  sales_invoice_lines: [['invoice_id'], ['sales_order_line_id']],
  credit_notes: [['invoice_id']],
  credit_note_lines: [['credit_note_id']],
  invoice_line_products: [['invoice_id'], ['invoice_line_id']],
  invoice_prints: [['invoice_id']],
  payments: [['customer_id'], ['payment_date']],
  payment_allocations: [['payment_id'], ['invoice_id']],
  supplier_bills: [['supplier_id'], ['goods_receipt_id'], ['bill_date']],
  supplier_payments: [['supplier_id'], ['payment_date']],
  supplier_credits: [['supplier_id'], ['goods_receipt_id']],
  supplier_credit_lines: [['supplier_credit_id']],
  supplier_allocations: [['bill_id'], ['payment_id'], ['credit_id']],
  stock_movements: [
    ['product_id', 'location_id'],
    ['ref_type', 'ref_id'],
    ['occurred_at'],
    ['batch_id'],
  ],
}

/**
 * Local unique indexes that mirror server uniqueness a device could violate
 * on its own before syncing (a counter's document numbers).
 */
export const LOCAL_UNIQUE_INDEXES: Partial<Record<TableName, readonly (readonly string[])[]>> = {
  sales_invoices: [['counter_id', 'fiscal_year', 'seq']],
  credit_notes: [['counter_id', 'fiscal_year', 'seq']],
}

function columnSql(name: string, col: Column): string {
  const notNull = !col.nullable && !col.server && name !== 'id'
  return `${name} ${SQLITE_TYPE[col.kind]}${name === 'id' ? ' PRIMARY KEY' : ''}${notNull ? ' NOT NULL' : ''}`
}

export function createTableSql(table: Table): string {
  const cols = Object.entries(table.columns).map(([name, col]) => `  ${columnSql(name, col)}`)
  return `CREATE TABLE IF NOT EXISTS ${table.name} (\n${cols.join(',\n')}\n) STRICT;`
}

/** Full local schema for the synced tables, in apply order. */
export function localSchemaSql(): string[] {
  const statements: string[] = []
  for (const table of syncedTables) {
    statements.push(createTableSql(table))
    for (const cols of LOCAL_INDEXES[table.name] ?? []) {
      statements.push(
        `CREATE INDEX IF NOT EXISTS ${table.name}_${cols.join('_')}_idx ON ${table.name} (${cols.join(', ')});`,
      )
    }
    for (const cols of LOCAL_UNIQUE_INDEXES[table.name] ?? []) {
      statements.push(
        `CREATE UNIQUE INDEX IF NOT EXISTS ${table.name}_${cols.join('_')}_uq ON ${table.name} (${cols.join(', ')}) WHERE ${cols.at(-1)} IS NOT NULL;`,
      )
    }
  }
  statements.push(
    `CREATE VIEW IF NOT EXISTS batch_stock AS
  SELECT product_id, location_id, batch_id, SUM(qty_milli) AS qty_milli
  FROM stock_movements WHERE deleted_at IS NULL AND batch_id IS NOT NULL
  GROUP BY product_id, location_id, batch_id;`,
  )
  statements.push(
    `CREATE VIEW IF NOT EXISTS current_stock AS
  SELECT product_id, location_id, SUM(qty_milli) AS qty_milli, MAX(occurred_at) AS last_movement_at
  FROM stock_movements WHERE deleted_at IS NULL
  GROUP BY product_id, location_id;`,
  )
  return statements
}

export type SqliteValue = string | number | null

/** Converts a row to SQLite values (booleans 0/1, JSON as text). Unknown keys are ignored. */
export function encodeRow(name: TableName, row: object): Record<string, SqliteValue> {
  const values = row as Record<string, unknown>
  const out: Record<string, SqliteValue> = {}
  for (const [key, col] of Object.entries(tablesByName[name].columns)) {
    const value = values[key]
    if (value === undefined || value === null) out[key] = null
    else if (col.kind === 'bool') out[key] = value ? 1 : 0
    else if (col.kind === 'json') out[key] = JSON.stringify(value)
    else out[key] = value as string | number
  }
  return out
}

/** Converts a SQLite row back (0/1 become booleans, JSON text is parsed). */
export function decodeRow(
  name: TableName,
  row: Record<string, SqliteValue>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, col] of Object.entries(tablesByName[name].columns)) {
    const value = row[key] ?? null
    if (value === null) out[key] = null
    else if (col.kind === 'bool') out[key] = value === 1
    else if (col.kind === 'json') out[key] = JSON.parse(String(value))
    else out[key] = value
  }
  return out
}

/** Parameterised upsert by id. Pair with encodeRow for the values. */
export function upsertSql(name: TableName): { sql: string; columns: string[] } {
  const columns = Object.keys(tablesByName[name].columns)
  const updates = columns.filter((c) => c !== 'id').map((c) => `${c} = excluded.${c}`)
  return {
    sql: `INSERT INTO ${name} (${columns.join(', ')}) VALUES (${columns.map(() => '?').join(', ')}) ON CONFLICT (id) DO UPDATE SET ${updates.join(', ')}`,
    columns,
  }
}

/** Stable fingerprint of everything that shapes the local schema. */
export function localSchemaFingerprint(): string {
  const text = JSON.stringify([localSchemaSql(), LOCAL_INDEXES, LOCAL_UNIQUE_INDEXES])
  // FNV-1a, 32-bit
  let hash = 0x811c9dc5
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193) >>> 0
  }
  return hash.toString(16).padStart(8, '0')
}
